import { useState, FormEvent, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import ModalComponent from "components/modal";
import { emptyExpenseRow, ExpenseItem } from "../detail";
import ActionButton from "components/table/action-btn";
import { IconMinus } from "@tabler/icons-react";
import { getLocationsByUser } from "api/location";
import { getTemplatesByLocation } from "api/template";
import { LocationType, Template } from "types/dict";
import { useSharedState } from "state";
import { createDocumentRequest, uploadDocumentFiles } from "api/document";

type FormErrors = Record<string, boolean>;
type FormRecords = Record<string, string>;

const REQUIRED_FIELDS = [
  "location_id",
  "template_id",
  "contragent",
  "lease_number",
  "contract_start_date",
  "contract_end_date",
  "description",
  "file",
] as const;

const PAYMENT_TYPES = [
  {
    value: "",
    label: "Вид оплаты",
    disabled: true,
  },
  {
    value: "Безналичная оплата",
    label: "Безналичная оплата",
    disabled: false,
  },
  {
    value: "Наличная оплата",
    label: "Наличная оплата",
    disabled: false,
  },
];

const getInputErrorMessage = (fieldId: string) =>
  fieldId === "file" ? "Добавьте договор" : "Введите корректное значение";

export default function NewDocument() {
  const { budgetTypes, getBudgetTypesData, getDocumentsData, notify } = useSharedState();

  const [formRecords, setFormRecords] = useState<FormRecords>({});
  const [errors, setErrors] = useState<FormErrors>({});
  const [fileList, setFileList] = useState<FileList | null>(null);

  const [userLocations, setUserLocations] = useState<LocationType[]>([]);
  const [userTemplates, setUserTemplates] = useState<Template[]>([]);

  const [isFormLoading, setFormLoading] = useState<boolean>(false);
  const [isFormSubmitted, setFormSubmitted] = useState<boolean>(false);

  const documentFields = [
    {
      id: "location_id",
      title: "Филиал",
      type: "select",
      placeholder: undefined,
      options: [
        { value: "", label: "Выберите филиал", disabled: true },
        ...userLocations
          .filter((loc) => loc.status)
          .map((loc) => ({ value: loc.location_id, label: loc.location, disabled: false })),
      ],
    },
    {
      id: "template_id",
      title: "Шаблон",
      type: "select",
      placeholder: undefined,
      options: [
        {
          value: "",
          label: formRecords.location_id ? "Выберите шаблон" : "Сначала выберите филиал",
          disabled: true,
        },
        ...userTemplates.map((temp) => ({ value: temp.template_id, label: temp.name, disabled: false })),
      ],
    },
    {
      id: "contragent",
      title: "Контрагент",
      type: "text",
      placeholder: "Контрагент",
      options: undefined,
    },
    {
      id: "lease_number",
      title: "Номер договора",
      type: "text",
      placeholder: "Номер договора",
      options: undefined,
    },
    {
      id: "contract_start_date",
      title: "Дата начала действия договора",
      type: "date",
      placeholder: undefined,
      options: undefined,
    },
    {
      id: "contract_end_date",
      title: "Дата окончания действия договора",
      type: "date",
      placeholder: undefined,
      options: undefined,
    },
    {
      id: "description",
      title: "Информация по договору",
      type: "textarea",
      placeholder: "Впишите краткое описание",
      options: undefined,
    },
    {
      id: "file",
      title: "Загрузить договоры",
      type: "file",
      placeholder: undefined,
      options: undefined,
    },
  ] as const;

  useEffect(() => {
    getLocationsData();
    getBudgetTypesData();
  }, []);

  useEffect(() => {
    formRecords.location_id && getTemplatesData();
    formRecords.template_id = "";
  }, [formRecords.location_id]);

  const getLocationsData = async () => {
    try {
      const userLocationsData: LocationType[] = await getLocationsByUser();
      setUserLocations(userLocationsData);
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при получении филиалов");
    }
  };

  const getTemplatesData = async () => {
    try {
      const userTemplates = await getTemplatesByLocation("Договоры", +formRecords.location_id);
      setUserTemplates(userTemplates);
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при получении шаблонов");
    }
  };

  const validateForm = () => {
    const newErrors: FormErrors = {};
    REQUIRED_FIELDS.forEach((field) => {
      if (!formRecords[field]?.trim()) {
        newErrors[field] = true;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    const files = (e.target as HTMLInputElement).files;

    files && setFileList(files);

    setFormRecords((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: false }));
  };

  const submitDocument = async (e: FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      notify("Заполните все поля", "danger");
      return;
    }

    setFormLoading(true);
    try {
      const formData = new FormData();

      const documentForm = {
        location_id: +formRecords.location_id,
        template_id: +formRecords.template_id,
        choosen_location: userLocations.find((item) => item.location_id === +formRecords.location_id)?.location,
        template_name: userTemplates.find((item) => item.template_id === +formRecords.template_id)?.name,
        contragent: formRecords.contragent,
        lease_number: formRecords.lease_number,
        contract_start_date: formRecords.contract_start_date,
        contract_end_date: formRecords.contract_end_date,
        description: formRecords.description,
      };

      if (fileList) {
        Array.from(fileList).forEach((file) => {
          formData.append(`files`, file);
        });
      }

      const request_id = await createDocumentRequest(documentForm);
      await uploadDocumentFiles(request_id, formData);
      notify("Договор создан", "success");

      setFormSubmitted(true);
      getDocumentsData();
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при создании договора");
    } finally {
      setFormLoading(false);
    }
  };

  const renderField = ({ id, title, type, placeholder, options }: (typeof documentFields)[number]) => {
    const Component = type === "textarea" ? "textarea" : "input";
    return (
      <div className={id === "description" || id === "file" ? "col-lg-12" : "col-lg-4"} key={id}>
        <div className="mb-3">
          <label className="form-label required">{title}</label>
          <div className="input-icon mb-3">
            {type === "select" ? (
              <select
                className={`form-select ${errors[id] ? "is-invalid" : ""}`}
                name={id}
                defaultValue={""}
                value={formRecords[id]}
                onChange={handleInputChange}
                disabled={isFormLoading}
              >
                {options.map((opt, index) => (
                  <option key={index} value={opt.value} disabled={opt.disabled}>
                    {opt.label}
                  </option>
                ))}
              </select>
            ) : (
              <Component
                type={type}
                className={`form-control ${errors[id] ? "is-invalid" : ""}`}
                name={id}
                max={type === "date" ? "9999-12-31" : undefined}
                placeholder={placeholder}
                value={formRecords[id] || ""}
                onChange={handleInputChange}
                disabled={isFormLoading}
                multiple={type === "file"}
              />
            )}

            {errors[id] && <div className="invalid-feedback">{getInputErrorMessage(id)}</div>}
            {isFormLoading && (
              <span className="input-icon-addon">
                <div className="spinner-border spinner-border-sm text-secondary" role="status" />
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  const newDocumentBody = (
    <form id="documentForm" className="row" onSubmit={submitDocument}>
      {documentFields.map(renderField)}
    </form>
  );

  const newDocumentFooter = (
    <button type="submit" form="documentForm" className="btn btn-success d-flex gap-1" disabled={isFormLoading}>
      Создать договор
      {isFormLoading && <div className="spinner-border spinner-border-sm" role="status" />}
    </button>
  );

  return (
    <ModalComponent
      header="Новый договор"
      body={newDocumentBody}
      footer={newDocumentFooter}
      disableClose={isFormLoading}
      shouldModalClose={isFormSubmitted}
    />
  );
}
